import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/app-shell'
import { AuthGuard } from './components/auth-guard'
import { AuthProvider } from './features/auth/auth-provider'
import { WorkspaceProvider } from './features/workspace/workspace-provider'
import { LoginPage } from './pages/auth/login'
import { SignupPage } from './pages/auth/signup'
import { VerifyPage } from './pages/auth/verify'
import { InvitePage } from './pages/invite'
import { ConnectPage } from './pages/verbinden'
import { DemoPage } from './pages/demo'
import { OnboardingPage } from './pages/onboarding'
import { AdminPage } from './pages/admin'
import { StartPage } from './pages/start'
import { ChatPage } from './pages/chat'
import { AutomatisierungenPage } from './pages/automatisierungen'
import { VaultPage } from './pages/vault'
import { WikiPage } from './pages/wiki'
import { OutputsPage } from './pages/outputs'
import { FreigabenPage } from './pages/freigaben'
import { QuellenregisterPage } from './pages/quellenregister'
import { GutachtenPage } from './pages/gutachten'
import { SettingsLayout } from './pages/settings'
import { SettingsBrandPage } from './pages/settings/brand'
import { SettingsContextPage } from './pages/settings/context'
import { SettingsConnectorsPage } from './pages/settings/connectors'
import { SettingsProfilePage } from './pages/settings/profile'
import { SettingsTeamPage } from './pages/settings/team'
import { SettingsUsagePage } from './pages/settings/usage'
import { SettingsBillingPage } from './pages/settings/billing'
import { SettingsConnectedAppsPage } from './pages/settings/connected-apps'
import { BillingSuccessPage } from './pages/billing/success'
import { BillingCancelPage } from './pages/billing/cancel'
import { NotFoundPage } from './pages/not-found'
import { PrivacyPage } from './pages/legal/privacy'
import { TermsPage } from './pages/legal/terms'
import { ImpressumPage } from './pages/legal/impressum'
import { applyTheme, useThemeStore } from './store/theme'

export default function App() {
  const theme = useThemeStore((state) => state.theme)

  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system') return

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const listener = () => applyTheme('system')
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [theme])

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/signup" element={<SignupPage />} />
          <Route path="/auth/verify" element={<VerifyPage />} />
          <Route path="/einladung/:token" element={<InvitePage />} />
          <Route path="/verbinden" element={<ConnectPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/billing/success" element={<BillingSuccessPage />} />
          <Route path="/billing/cancel" element={<BillingCancelPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/impressum" element={<ImpressumPage />} />

          <Route
            element={
              <AuthGuard>
                <WorkspaceProvider>
                  <AppShell />
                </WorkspaceProvider>
              </AuthGuard>
            }
          >
            <Route index element={<StartPage />} />
            <Route path="/fragen" element={<ChatPage />} />
            <Route path="/quellen" element={<VaultPage />} />
            <Route path="/automatisierungen" element={<AutomatisierungenPage />} />
            <Route path="/wissen" element={<WikiPage />} />
            <Route path="/freigaben" element={<FreigabenPage />} />
            <Route path="/quellenregister" element={<QuellenregisterPage />} />
            <Route path="/gutachten" element={<GutachtenPage />} />
            <Route path="/outputs" element={<OutputsPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/einstellungen" element={<SettingsLayout />}>
              <Route index element={<Navigate to="/einstellungen/profil" replace />} />
              <Route path="profil" element={<SettingsProfilePage />} />
              <Route path="team" element={<SettingsTeamPage />} />
              <Route path="usage" element={<SettingsUsagePage />} />
              <Route path="billing" element={<SettingsBillingPage />} />
              <Route path="brand" element={<SettingsBrandPage />} />
              <Route path="context" element={<SettingsContextPage />} />
              <Route path="connectors" element={<SettingsConnectorsPage />} />
              <Route path="verbundene-apps" element={<SettingsConnectedAppsPage />} />
            </Route>

            {/* Legacy redirects */}
            <Route path="/chat" element={<Navigate to="/fragen" replace />} />
            <Route path="/vault" element={<Navigate to="/quellen" replace />} />
            <Route path="/wiki" element={<Navigate to="/wissen" replace />} />
            <Route path="/skills" element={<Navigate to="/automatisierungen" replace />} />
            <Route path="/settings" element={<Navigate to="/einstellungen" replace />} />
            <Route path="/settings/*" element={<Navigate to="/einstellungen" replace />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
